<template>
    <div class="browser-wrapper">
        <!-- Browser Header (Apple-style) -->
        <div class="browser-header">
            <div class="browser-buttons">
                <span class="browser-button close"></span>
                <span class="browser-button minimize"></span>
                <span class="browser-button maximize"></span>
            </div>
            <div class="browser-url-bar">
                <span class="lock-icon">🔒</span>
                <input
                    type="text"
                    class="url-input"
                    :value="computedUrl"
                    readonly
                />
            </div>
        </div>

        <!-- Debugging Output -->
        <!-- <pre>{{ localBranding }}</pre> -->

        <!-- Preview Content -->
        <div class="browser-content">
            <v-container fluid class="preview-container">
                <v-row class="full-height-row">
                    <!-- Sidebar Mock -->
                    <v-col cols="3" class="mock-sidebar" :style="sidebarStyle">
                        <div class="logo-container">
                            <img
                                :src="mockLogo"
                                alt="Mock Logo"
                                class="mock-logo"
                            />
                        </div>

                        <v-list>
                            <v-list-item-group
                                v-model="selectedStep"
                                color="primary"
                                class="sidebar-list"
                            >
                                <v-list-item
                                    v-for="(step, index) in steps"
                                    :key="index"
                                >
                                    <!-- Step Number Circle -->
                                    <v-list-item-icon>
                                        <span
                                            class="step-number"
                                            :class="{ active: selectedStep === index }"
                                            :style="{
                                                backgroundColor: selectedStep === index ? primaryColor : '#ccc',
                                                color: selectedStep === index ? '#fff' : '#000'
                                            }"
                                        >
                                            {{ index + 1 }}
                                        </span>
                                    </v-list-item-icon>

                                    <!-- Step Title -->
                                    <v-list-item-content>
                                        <v-list-item-title>{{
                                            step
                                        }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </v-col>

                    <!-- Main Content Mock -->
                    <v-col cols="9" class="preview-content">
                        <h2 class="title" :style="textStyle">
                            How much do you want to borrow?
                        </h2>
                        <v-text-field
                            label="Type amount"
                            outlined
                            dense
                        ></v-text-field>

                        <v-btn
                            v-for="(amount, index) in amounts"
                            :key="amount"
                            outlined
                            class="amount-btn"
                            :color="index === 0 ? secondaryColor : undefined"
                        >
                            $ {{ amount }}
                        </v-btn>

                        <!-- Mock Help Card -->
                        <v-card class="mock-help" elevation="0">
                            <v-card-title class="help-title"
                                >We are here to help.</v-card-title
                            >
                            <v-card-text>
                                Simply enter the amount you would like to
                                borrow.
                                <br />— <strong>Zinky</strong> from
                                <strong>{{ name }}</strong>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Footer Mock -->
                <v-footer class="preview-footer">
                    <!-- Back Button: Outlined -->
                    <v-btn
                        outlined
                        class="back-btn"
                        :style="{
                            borderColor: primaryColor,
                            color: primaryColor,
                        }"
                    >
                        ⬅ BACK
                    </v-btn>

                    <!-- Continue Button: Filled -->
                    <v-btn
                        class="continue-btn"
                        :style="{
                            backgroundColor: primaryColor,
                            color: '#fff',
                        }"
                    >
                        CONTINUE
                    </v-btn>
                </v-footer>
            </v-container>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        branding: Object, // Branding object now includes the `urlKey`
    },
    data() {
        return {
            selectedStep: 0,
            steps: ["Step 1", "Step 2", "Step 3"],
            amounts: [50000, 40000, 30000, 20000, 10000],
            localBranding: {},
        };
    },
    mounted() {
        this.localBranding = { ...this.branding };
    },
    computed: {
        sidebarStyle() {
            console.log("Sidebar Color:", this.localBranding?.sidebarColor);
            return {
                backgroundColor: this.localBranding?.sidebarColor || "#EAFBF1",
                transition: "background-color 0.3s ease",
            };
        },
        textStyle() {
            return {
                color: this.localBranding?.textColor || "#333",
                fontFamily: this.localBranding?.fontFamily || "Arial",
            };
        },
        buttonStyle() {
            return {
                backgroundColor:
                    this.localBranding?.primaryButtonColor || "#2196F3",
                color: "#fff",
                transition: "background-color 0.3s ease",
            };
        },
        mockLogo() {
            return (
                this.localBranding?.logo || "https://via.placeholder.com/150"
            );
        },
        computedUrl() {
            // URL key now comes from branding metadata
            const urlKey = this.localBranding?.urlKey || "preview";
            return `https://leads.zink.com.au/${urlKey}`;
        },
        primaryColor() {
            return this.localBranding?.primaryColor;
        },
        secondaryColor() {
            return this.localBranding?.secondaryColor;
        },
        name() {
            return this.localBranding?.name;
        },
    },
    watch: {
        branding: {
            deep: true,
            handler(newBranding) {
                this.$set(this, "localBranding", { ...newBranding });
            },
        },
    },
};
</script>

<style scoped>
/* 🖥 Apple-style Browser Container */
.browser-wrapper {
    width: 800px;
    height: 600px;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    border: 1px solid #ddd;
}

/* 🍏 Apple-style Browser Header */
.browser-header {
    height: 40px;
    background: #f5f5f7;
    display: flex;
    align-items: center;
    padding: 8px;
    border-bottom: 1px solid #ddd;
}

/* 🔴 🟡 🟢 Browser Buttons */
.browser-buttons {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 10px;
}
.browser-button {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
}
.close {
    background: #ff5f57;
}
.minimize {
    background: #ffbd2e;
}
.maximize {
    background: #28c840;
}

/* 🔒 URL Bar */
.browser-url-bar {
    flex: 1;
    display: flex;
    align-items: center;
    margin-left: 15px;
    background: #fff;
    border-radius: 8px;
    padding: 4px 10px;
    border: 1px solid #ccc;
}
.lock-icon {
    margin-right: 8px;
    font-size: 14px;
}
.url-input {
    border: none;
    background: transparent;
    font-size: 14px;
    width: 100%;
    color: #333;
    outline: none;
}

/* 📜 Browser Content */
.browser-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    background: white;
}

/* 📑 Preview Container */
.preview-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
}

/* 📏 Sidebar */
.mock-sidebar {
    height: 100%;
    padding: 15px;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: background-color 0.3s ease;
}

/* 🖼 Logo */
.mock-logo {
    max-width: 100px;
    margin: 10px auto;
    display: block;
}

/* 📑 Main Content */
.preview-content {
    padding: 20px;
    flex-grow: 1;
}

/* 📌 Title */
.title {
    font-size: 20px;
    font-weight: bold;
}

/* 💵 Amount Buttons */
.amount-btn {
    margin: 5px;
}

/* 💬 Help Card */
.mock-help {
    margin-top: 20px;
    padding: 15px;
    background: #f5f5f5;
    border-radius: 5px;
}

/* 📏 Footer */
.preview-footer {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 15px;
    background: #f9f9f9;
    border-top: 1px solid #ddd;
    flex-shrink: 0;
}

/* 🎨 Buttons */
.back-btn {
    border: 1px solid #ccc;
}
.continue-btn {
    transition: background-color 0.3s ease;
}

/* Step Number Circle */
.step-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #ccc;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
    transition: all 0.3s ease;
}

/* Selected Step - Turns Red */
.step-number.active {
    background: red;
}

/* Remove padding & background from v-list */
.sidebar-list {
    background: transparent !important; /* Removes default background */
    padding: 0 !important; /* Removes extra padding */
}

/* Remove padding from v-list items */
.v-list-item {
    padding: 4px 0 !important; /* Reduce padding */
    min-height: 36px !important; /* Reduce height */
}

/* Reduce icon padding */
.v-list-item-icon {
    min-width: 30px !important; /* Adjust spacing for number */
    margin-right: 8px; /* Reduce space between icon & text */
}

.v-list {
    background-color: transparent !important;
}
</style>
